/**
 * La scheda Info della partita conclusa, la prima: il risultato con le due
 * squadre, prima quella di casa, sotto ciascuna i suoi marcatori con il
 * minuto e in fondo al riquadro ammoniti ed espulsi; poi le informazioni
 * della partita (competizione, luogo, formato, note) e le statistiche di
 * squadra.
 *
 * I cartellini sono solo nostri: dell'avversario si registrano solo i gol.
 */

import type { Tabellino as DatiTabellino, TipoEvento } from '@calcio/shared';
import { InfoIcon, TrophyIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { CardVista } from '../comuni/Riquadri.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { useContesto } from '../navigazione/contesto.js';
import { usePartita } from './contesto.js';
import { IconaEvento } from './eventi.js';
import { ElencoInformazioni } from './InfoPartita.js';
import type { NomeGiocatore } from './partita.js';
import { StatisticheSquadra } from './Tabellino.js';

/** I marcatori sotto le squadre: i gol delle due squadre, autogol compresi. */
const GOL: TipoEvento[] = ['gol', 'autogol_avversario', 'gol_subito', 'autogol'];
/** Le voci che vanno sotto l'avversario: i suoi gol, compresi i nostri autogol. */
const DELL_AVVERSARIO: TipoEvento[] = ['gol_subito', 'autogol'];

interface Riga {
  id: string;
  tipo: TipoEvento;
  testo: string;
  minuto: string;
}

export function SchedaInfo() {
  const { ss } = useParams<{ ss: string }>();
  const { squadre } = useContesto();
  const partita = usePartita();
  if (!partita?.tabellino) return <StatoCaricamento errore={partita?.errore ?? null} />;

  const { partita: p, formato, tabellino, giocatori } = partita.tabellino;
  const noi = squadre?.find((s) => s.id === ss)?.squadra ?? 'Noi';
  const { nostre, loro, gialli, rossi } = righe(tabellino, giocatori);
  const casa = p.inCasa
    ? { nome: noi, gol: tabellino.risultato.golFatti, righe: nostre }
    : { nome: p.avversario, gol: tabellino.risultato.golSubiti, righe: loro };
  const ospiti = p.inCasa
    ? { nome: p.avversario, gol: tabellino.risultato.golSubiti, righe: loro }
    : { nome: noi, gol: tabellino.risultato.golFatti, righe: nostre };

  return (
    <div className="grid items-start gap-4 @5xl:grid-cols-2">
      <CardVista titolo="Risultato" Icona={TrophyIcon} className="@5xl:col-span-2">
        <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-x-3 gap-y-3 rounded-lg border border-border bg-card p-3 @md:gap-x-4 @md:p-4">
          <Squadra nome={casa.nome} etichetta="Casa" allinea="destra" />
          <span className="self-center text-2xl font-bold whitespace-nowrap tabular-nums @md:text-4xl">
            {casa.gol} – {ospiti.gol}
          </span>
          <Squadra nome={ospiti.nome} etichetta="Ospiti" allinea="sinistra" />
          <ElencoRighe righe={casa.righe} allinea="destra" />
          <span />
          <ElencoRighe righe={ospiti.righe} allinea="sinistra" />
          {(gialli.length > 0 || rossi.length > 0) && (
            <div className="col-span-3 flex flex-col items-center gap-1 border-t border-border pt-3 text-sm">
              <Cartellini tipo="cartellino_giallo" etichetta="Ammoniti" righe={gialli} />
              <Cartellini tipo="cartellino_rosso" etichetta="Espulsi" righe={rossi} />
            </div>
          )}
        </div>
      </CardVista>
      <CardVista titolo="Informazioni" Icona={InfoIcon}>
        <div className="rounded-lg border border-border bg-card p-4">
          <ElencoInformazioni partita={p} formato={formato} />
        </div>
      </CardVista>
      <StatisticheSquadra tabellino={tabellino} />
    </div>
  );
}

/** Gol e autogol divisi tra noi e l'avversario, e i nostri cartellini, in ordine di minuto. */
function righe(tabellino: DatiTabellino, giocatori: NomeGiocatore[]) {
  const per = new Map(giocatori.map((g) => [g.id, g]));
  const nostre: Riga[] = [];
  const loro: Riga[] = [];
  const gialli: Riga[] = [];
  const rossi: Riga[] = [];
  for (const v of tabellino.voci) {
    if (v.tipo === 'sostituzione') continue;
    const cartellino =
      v.tipo === 'cartellino_giallo' ? gialli : v.tipo === 'cartellino_rosso' ? rossi : null;
    if (!cartellino && !GOL.includes(v.tipo)) continue;
    // Senza numero di maglia: nel riquadro lo spazio è poco, su telefono.
    const g = v.giocatoreId ? per.get(v.giocatoreId) : undefined;
    const nome = g ? `${g.cognome} ${g.nome.charAt(0)}.` : null;
    const testo =
      v.tipo === 'gol_subito'
        ? 'Gol'
        : v.tipo === 'autogol_avversario'
          ? 'Autogol'
          : v.tipo === 'autogol'
            ? `Autogol di ${nome ?? '—'}`
            : (nome ?? '—');
    const riga = { id: v.eventoId, tipo: v.tipo, testo, minuto: v.etichetta };
    (cartellino ?? (DELL_AVVERSARIO.includes(v.tipo) ? loro : nostre)).push(riga);
  }
  return { nostre, loro, gialli, rossi };
}

function Squadra({
  nome,
  etichetta,
  allinea,
}: {
  nome: string;
  etichetta: string;
  allinea: 'destra' | 'sinistra';
}) {
  return (
    <span
      className={cn(
        'flex min-w-0 flex-col',
        allinea === 'destra' ? 'items-end text-right' : 'items-start',
      )}
    >
      <span className="text-[12px] tracking-wide text-muted-foreground uppercase">{etichetta}</span>
      <span className="text-base font-semibold break-words @md:text-lg">{nome}</span>
    </span>
  );
}

function ElencoRighe({ righe, allinea }: { righe: Riga[]; allinea: 'destra' | 'sinistra' }) {
  if (righe.length === 0) return <span />;
  return (
    <ul className={cn('flex min-w-0 flex-col gap-1 text-sm', allinea === 'destra' && 'items-end')}>
      {righe.map((r) => (
        <li
          key={r.id}
          className={cn(
            'flex max-w-full flex-wrap items-center gap-x-1.5',
            allinea === 'destra' && 'flex-row-reverse',
          )}
        >
          <span className="whitespace-nowrap">{r.testo}</span>
          <span className="shrink-0 text-muted-foreground tabular-nums">{r.minuto}</span>
        </li>
      ))}
    </ul>
  );
}

/** Una riga del fondo del riquadro: il cartellino, l'etichetta e i giocatori col minuto. */
function Cartellini({
  tipo,
  etichetta,
  righe,
}: {
  tipo: TipoEvento;
  etichetta: string;
  righe: Riga[];
}) {
  if (righe.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-center">
      <IconaEvento tipo={tipo} />
      <span className="text-muted-foreground">{etichetta}</span>
      {righe.map((r, i) => (
        <span key={r.id} className="whitespace-nowrap">
          {r.testo} <span className="text-muted-foreground tabular-nums">{r.minuto}</span>
          {i < righe.length - 1 && ','}
        </span>
      ))}
    </p>
  );
}
